import { signal } from '@angular/core';
import { nextRailDock } from '../../core/rail/rail.dock';
import { railPinPreference } from '../../core/rail/rail.pin.preference';

/**
 * How the Details rail occupies the row beside the document. `inline` reserves the full rail column,
 * `strip` and `overlay` reserve only the dock strip (an overlay floats above the document), `none` reserves nothing.
 */
export type FinancialDocumentRailLayout = 'none' | 'strip' | 'overlay' | 'inline';

export interface FinancialDocumentRailState {
  visible: boolean;
  /** Narrow host (`nextRailDock`): an unpinned expanded rail floats instead of taking a column. */
  docked: boolean;
  pinned: boolean;
  collapsed: boolean;
}

/**
 * One owner for the rail layout. The LESS keys off this value alone, so the column the document preview
 * measures (and fits to) always matches the pin control and outside-click policy — a CSS breakpoint of its
 * own would disagree with the dock hysteresis and let the rail overlay or squeeze the wrong state.
 */
export function financialDocumentRailLayout(state: FinancialDocumentRailState): FinancialDocumentRailLayout {

  if (!state.visible) return 'none';
  if (state.collapsed) return 'strip';
  if (state.docked && !state.pinned) return 'overlay';
  return 'inline';
}

/** What a remount needs to paint the rail exactly as the previous record left it. */
export interface FinancialDocumentRailSeed {
  docked: boolean;
  /** Session-only: a narrow host's expand/collapse choice, never written to the wide preference. */
  dockedCollapsed: boolean;
}

export interface FinancialDocumentRailMemory {
  read(): FinancialDocumentRailSeed | undefined;
  write(seed: FinancialDocumentRailSeed): void;
}

/** Responsive collapse must not overwrite the user's wide-layout choice when a document remounts. */
export function createFinancialDocumentRailState(memory?: FinancialDocumentRailMemory) {

  const pinPreference = railPinPreference('wb.fd.rail.pinned');
  // Reuse the boolean preference storage, but expansion is independent of pinning.
  const collapsePreference = railPinPreference('wb.fd.rail.collapsed');
  const seed = memory?.read();
  let wideCollapsed = collapsePreference.read();
  // A seed is the previous mount's measurement, so the first render already has the real dock state.
  let measured = seed !== undefined;
  const docked = signal(seed?.docked ?? false);
  const pinned = signal(pinPreference.read());
  // Apply the saved choice on the first render; starting closed makes wide document switches flicker.
  const collapsed = signal(seed?.docked ? seed.dockedCollapsed : wideCollapsed);

  // Tracks the narrow-host choice only; the wide choice already lives in the collapse preference.
  let dockedCollapsed = seed?.dockedCollapsed;
  const remember = () => {

    if (!measured) return;
    if (docked()) dockedCollapsed = collapsed();
    memory?.write({ docked: docked(), dockedCollapsed: dockedCollapsed ?? !pinned() });
  };

  return {
    docked: docked.asReadonly(),
    pinned: pinned.asReadonly(),
    collapsed: collapsed.asReadonly(),
    resize(width: number): void {

      const nextDocked = nextRailDock(width, docked());
      if (!measured || nextDocked !== docked()) {
        docked.set(nextDocked);
        // Crossing into the narrow layout inside one mount still closes the overlay so it cannot cover the document.
        collapsed.set(nextDocked ? !pinned() : wideCollapsed);
        measured = true;
        remember();
      }
    },
    setCollapsed(next: boolean): void {

      collapsed.set(next);
      if (measured && !docked()) {
        wideCollapsed = next;
        collapsePreference.write(next);
      }
      remember();
    },
    setPinned(next: boolean): void {

      pinned.set(next);
      pinPreference.write(next);
      if (next) collapsed.set(false);
      remember();
    },
  };
}

/**
 * The remembered tab survives records it is hidden on (Payments on a Draft), so the next record that shows it
 * opens there again; undefined lets the tab shell fall back to the first visible tab.
 */
export function financialDocumentRailTab(tabs: readonly { value: string; visible?: boolean }[], remembered: string | undefined): string | undefined {

  return tabs.find(tab => tab.value === remembered && tab.visible !== false)?.value;
}

/** The tab shell reports its first-visible fallback as a selection; that must not replace a tab the record merely hides. */
export function nextRememberedRailTab(tabs: readonly { value: string; visible?: boolean }[], remembered: string | undefined, selected: string | undefined): string | undefined {

  if (!selected) return remembered;
  const rememberedHidden = tabs.some(tab => tab.value === remembered && tab.visible === false);
  const fallback = tabs.find(tab => tab.visible !== false)?.value;
  return rememberedHidden && selected === fallback ? remembered : selected;
}
